// Language toggle — EN ↔ Korean, powered by the Google Translate Element
// mounted in public/index.html. We control it via the `googtrans` cookie which
// Google Translate reads on every page load.
import React, { useCallback, useEffect, useState } from "react";
import { CaretDown, Translate, Check } from "@phosphor-icons/react";

// Domains that GTranslate cookie needs to be set on.
// Setting on both .shipkorea.in AND the current hostname covers preview + prod.
function setCookie(name, value, host) {
  const d = new Date();
  d.setTime(d.getTime() + 365 * 24 * 60 * 60 * 1000);
  document.cookie = `${name}=${value}; expires=${d.toUTCString()}; path=/`;
  if (host) document.cookie = `${name}=${value}; expires=${d.toUTCString()}; domain=${host}; path=/`;
}

function currentLang() {
  const m = document.cookie.match(/googtrans=\/en\/([a-z-]+)/i);
  return m ? m[1] : "en";
}

const LANGS = [
  { code: "en", label: "English", short: "EN", flag: "🇮🇳" },
  { code: "ko", label: "한국어", short: "한", flag: "🇰🇷" },
];

export function LanguageToggle({ compact = false }) {
  const [lang, setLang] = useState("en");
  const [open, setOpen] = useState(false);

  // On mount, read cookie and reflect current state on body class.
  useEffect(() => {
    const l = currentLang();
    setLang(l);
    document.body.classList.toggle("lang-ko", l === "ko");
  }, []);

  const switchTo = useCallback((code) => {
    // Set both root domain (production) and current host (preview).
    const host = window.location.hostname;
    const rootDomain = host.split(".").length >= 2 ? "." + host.split(".").slice(-2).join(".") : host;
    if (code === "en") {
      setCookie("googtrans", "", rootDomain);
      setCookie("googtrans", "", host);
    } else {
      setCookie("googtrans", `/en/${code}`, rootDomain);
      setCookie("googtrans", `/en/${code}`, host);
    }
    setLang(code);
    setOpen(false);
    // Reload so Google Translate re-initialises with new target lang.
    window.location.reload();
  }, []);

  const active = LANGS.find((l) => l.code === lang) || LANGS[0];

  return (
    <div className="relative notranslate" translate="no" data-testid="lang-toggle-wrap">
      <button
        onClick={() => setOpen((o) => !o)}
        data-testid="lang-toggle-btn"
        aria-label="Change language"
        className={`inline-flex items-center gap-1.5 rounded-full border border-slate-300 bg-white hover:border-korea-blue hover:text-korea-blue transition-colors font-semibold ${compact ? "h-9 px-3 text-xs" : "h-10 px-3.5 text-sm"}`}
      >
        <Translate weight="bold" className="h-4 w-4" />
        <span>{active.short}</span>
        <span aria-hidden>{active.flag}</span>
        <CaretDown className="h-3 w-3 opacity-60" />
      </button>
      {open && (
        <>
          {/* click-away layer */}
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div
            role="menu"
            data-testid="lang-toggle-menu"
            className="absolute right-0 mt-2 z-50 w-44 rounded-xl border border-slate-200 bg-white shadow-lg py-1"
          >
            {LANGS.map((l) => (
              <button
                key={l.code}
                onClick={() => switchTo(l.code)}
                data-testid={`lang-opt-${l.code}`}
                className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-korea-bluelight/40 ${lang === l.code ? "text-korea-blue font-semibold" : "text-slate-700"}`}
              >
                <span className="text-base leading-none" aria-hidden>{l.flag}</span>
                <span className="flex-1">{l.label}</span>
                {lang === l.code && <Check weight="bold" className="h-4 w-4 text-korea-blue" />}
              </button>
            ))}
            <div className="mt-1 pt-2 border-t border-slate-100 px-3 py-1.5 text-[10px] text-slate-400 leading-relaxed">
              Powered by Google Translate. Auto-translates every page.
            </div>
          </div>
        </>
      )}
    </div>
  );
}
